import { useEffect, useState, memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { House, CalendarBlank, QrCode, ChartBar, User, Scan } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { canUserScan } from '../utils/permissions'

function GroupLeaderBottomNav() {
  const location = useLocation()
  const [userId, setUserId] = useState(null)
  const [canScan, setCanScan] = useState(() => {
    // Initialize from cache immediately
    const cached = localStorage.getItem('leaderCanScan')
    return cached === 'true'
  })
  const [checkingPermission, setCheckingPermission] = useState(false)

  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id)
    }
    getUserId()
  }, [])

  // Check scanning permission when userId is available
  useEffect(() => {
    const checkScanPermission = async () => {
      if (!userId) {
        setCanScan(false)
        localStorage.removeItem('leaderCanScan')
        return
      }

      // Don't show loading if we have cached value
      const cached = localStorage.getItem('leaderCanScan')
      if (!cached) {
        setCheckingPermission(true)
      }

      const hasPermission = await canUserScan(userId)
      setCanScan(hasPermission)
      localStorage.setItem('leaderCanScan', hasPermission.toString())
      setCheckingPermission(false)
    }

    checkScanPermission()
  }, [userId])

  const isActive = (path) => location.pathname === path

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg safe-area-bottom z-50">
      <div className="grid grid-cols-5 h-16 transition-all duration-300">
        <Link
          to="/group-leader/home"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/group-leader/home')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <House size={24} weight={isActive('/group-leader/home') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/group-leader/home') ? 'font-bold' : ''}`}>Home</span>
        </Link>

        <Link
          to="/group-leader/events"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/group-leader/events')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarBlank size={24} weight={isActive('/group-leader/events') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/group-leader/events') ? 'font-bold' : ''}`}>Events</span>
        </Link>

        {/* Middle tab - Scanner or Code based on team permission */}
        {canScan ? (
          <Link
            to="/group-leader/scanner"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/group-leader/scanner')
                ? 'text-blue-600 border-t border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Scan size={24} weight={isActive('/group-leader/scanner') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/group-leader/scanner') ? 'font-bold' : ''}`}>Scan</span>
          </Link>
        ) : checkingPermission ? (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <QrCode size={24} weight="light" className="opacity-50" />
            <span className="text-xs mt-1">...</span>
          </div>
        ) : (
          <Link
            to="/group-leader/code"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/group-leader/code')
                ? 'text-blue-600 border-t border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <QrCode size={24} weight={isActive('/group-leader/code') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/group-leader/code') ? 'font-bold' : ''}`}>Code</span>
          </Link>
        )}

        <Link
          to="/group-leader/stats"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/group-leader/stats')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ChartBar size={24} weight={isActive('/group-leader/stats') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/group-leader/stats') ? 'font-bold' : ''}`}>Stats</span>
        </Link>

        <Link
          to="/group-leader/profile"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/group-leader/profile')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User size={24} weight={isActive('/group-leader/profile') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/group-leader/profile') ? 'font-bold' : ''}`}>Profile</span>
        </Link>
      </div>
    </nav>
  )
}

export default memo(GroupLeaderBottomNav)